/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Link } from 'react-router';

import { Box, Card, Divider, Typography } from '@mui/material';

import { AlignCenter } from '../../../../components/AlignCenter/AlignCenter';
import { HUModal2 } from '../../../../components/HUModal2/HUModal2';
import { BasicRoutes } from '../../../../constants/basicRoutes';
import { Namespaces } from '../../../../i18n';
import { prepareRoutePath } from '../../../../utils/prepareRoutePath';
import { fetchJiraRequest } from '../../../../utils/queryFns';
import { useFormatDate } from '../../../../utils/useFormatDate';
import { useJiraSupportStyles } from '../styles';
import { emptyTicket, ViewTicketModalProps } from '../types';
import { TicketComments } from './TicketComments';

export const ViewTicketModal = memo<ViewTicketModalProps>(({ ticketId, onClose }) => {
  const { t } = useTranslation([Namespaces.General]);
  const { formatDate } = useFormatDate();
  const classes = useJiraSupportStyles();

  const { data: ticketData, isLoading } = useQuery([fetchJiraRequest.key, { id: ticketId }], fetchJiraRequest.fn, {
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const ticket = useMemo(() => ticketData?.payload ?? emptyTicket, [ticketData]);

  if (!ticketId) {
    return null;
  }

  const getLabelKey = (key: string, label?: boolean) =>
    label ? `${Namespaces.General}:jira_support.labels.${key}` : `${Namespaces.General}:jira_support.${key}`;

  return (
    <HUModal2
      size="big"
      open={!isLoading}
      onCancel={onClose}
      disableRestoreFocus
      onClose={onClose}
      title={t(getLabelKey('issueDetails', true))}
      cancelButtonText={t(getLabelKey('close', true))}
    >
      <Box className={classes.gapOne}>
        <Box className={classes.root}>
          <AlignCenter className={classes.gapOne}>
            <Box style={{ display: 'flex', gap: '8px' }}>
              <Typography variant="subtitle2" component="span">
                {ticket.issueKey}
              </Typography>
              <Divider flexItem orientation="vertical" />
              <Typography variant="subtitle2" component="span">
                {formatDate(ticket.createdAt)}
              </Typography>
              <Divider flexItem orientation="vertical" />
              <Typography variant="subtitle2" component="span">
                {ticket.status.name}
              </Typography>
            </Box>
            {ticket.metaData['Cluster ID'] ? (
              <Box style={{ display: 'flex', gap: '8px' }}>
                <Link
                  target="_blank"
                  to={prepareRoutePath(BasicRoutes.CLUSTER_PAGE, {
                    ':organization_id': ticket.metaData['Organization ID'],
                    ':project_id': ticket.metaData['Project ID'],
                    ':cluster_id': ticket.metaData['Cluster ID'],
                  })}
                >
                  <Typography variant="subtitle1" component="span">
                    {t(getLabelKey('cluster', true))}: {ticket.metaData['Cluster Name']}
                  </Typography>
                </Link>
                <Divider flexItem orientation="vertical" />
                <Typography variant="subtitle1" component="span">
                  {t(getLabelKey('supportPlan', true))}: {ticket.metaData['Support Plan']}
                </Typography>
              </Box>
            ) : null}
          </AlignCenter>
          <Box className={classes.gapOne}>
            <Typography variant="h6" className={classes.header}>
              {t(getLabelKey('summary', true))}
            </Typography>
            <Card classes={{ root: classes.card }}>
              <Typography variant="body1">{ticket.summary}</Typography>
            </Card>
          </Box>
          <Box className={classes.gapOne}>
            <Typography variant="h6" className={classes.header}>
              {t(getLabelKey('description', true))}
            </Typography>
            <Card classes={{ root: classes.card }}>
              {ticket.description ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: ticket.description,
                  }}
                />
              ) : (
                <Typography>{t(getLabelKey('no_description'))}</Typography>
              )}
            </Card>
          </Box>
        </Box>
        <Box className={classes.gapOne}>
          <Typography variant="h6" className={classes.header}>
            {t(getLabelKey('comments', true))}
          </Typography>
          <TicketComments id={ticket.issueId} />
        </Box>
      </Box>
    </HUModal2>
  );
});
